import React, { useState, useEffect } from 'react'
import {
  Users,
  Building2,
  GraduationCap,
  Video,
  Phone,
  MessageCircle,
  Calendar,
  TrendingUp
} from 'lucide-react'
import { DashboardStats } from '@/types'
import { api } from '@/lib/api'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { formatNumber } from '@/lib/utils'

interface StatCardProps {
  title: string
  value: number
  icon: React.ElementType
  trend?: string
  color?: 'blue' | 'green' | 'purple' | 'orange' | 'red'
  loading?: boolean
}

const StatCard: React.FC<StatCardProps> = ({ 
  title, 
  value, 
  icon: Icon, 
  trend, 
  color = 'blue',
  loading = false
}) => {
  const colorClasses = {
    blue: 'bg-blue-500',
    green: 'bg-green-500',
    purple: 'bg-purple-500',
    orange: 'bg-orange-500',
    red: 'bg-red-500'
  }

  return (
    <div className="bg-white overflow-hidden shadow rounded-lg">
      <div className="p-5">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <div className={`h-8 w-8 rounded-md ${colorClasses[color]} flex items-center justify-center`}>
              <Icon className="h-4 w-4 text-white" />
            </div>
          </div>
          <div className="ml-5 w-0 flex-1">
            <dl>
              <dt className="text-sm font-medium text-gray-500 truncate">
                {title}
              </dt>
              <dd className="flex items-baseline">
                {loading ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  <>
                    <div className="text-2xl font-semibold text-gray-900">
                      {formatNumber(value)}
                    </div>
                    {trend && (
                      <div className="ml-2 flex items-baseline text-sm font-semibold text-green-600">
                        <TrendingUp className="h-3 w-3 mr-1" />
                        {trend}
                      </div>
                    )}
                  </>
                )}
              </dd>
            </dl>
          </div>
        </div>
      </div>
    </div>
  )
}

interface TimeFrameStats {
  studentsConnected: number
  sessionsConnected: number
  videoCallsConnected: number
  callsConnected: number
  chatConnected: number
  counselorsOnboarded?: number
  agenciesOnboarded?: number
}

interface StatsGridProps {
  title: string
  stats: TimeFrameStats
  loading: boolean
  showOnboarding?: boolean
}

const StatsGrid: React.FC<StatsGridProps> = ({ title, stats, loading, showOnboarding = false }) => {
  return (
    <div className="mb-8">
      <h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard
          title="Students Connected"
          value={stats.studentsConnected}
          icon={GraduationCap}
          color="blue"
          loading={loading}
        />
        <StatCard
          title="Sessions"
          value={stats.sessionsConnected}
          icon={Calendar}
          color="green"
          loading={loading}
        />
        <StatCard
          title="Video Calls"
          value={stats.videoCallsConnected}
          icon={Video}
          color="purple"
          loading={loading}
        />
        <StatCard
          title="Audio Calls"
          value={stats.callsConnected}
          icon={Phone}
          color="orange"
          loading={loading}
        />
        <StatCard
          title="Chats"
          value={stats.chatConnected}
          icon={MessageCircle}
          color="red"
          loading={loading}
        />
      </div>
      
      {showOnboarding && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <StatCard
            title="Counselors Onboarded"
            value={stats.counselorsOnboarded || 0}
            icon={Users}
            color="green"
            loading={loading}
          />
          <StatCard
            title="Agencies Onboarded"
            value={stats.agenciesOnboarded || 0}
            icon={Building2}
            color="blue"
            loading={loading}
          />
        </div>
      )}
    </div>
  )
}

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true)
        const response = await api.get<DashboardStats>('/admin/dashboard/stats')
        setStats(response)
        setError('')
      } catch (error) {
        console.error('Failed to fetch dashboard stats:', error)
        setError('Failed to load dashboard statistics')
        // Set default/mock data for development
        setStats({
          today: {
            studentsConnected: 0,
            sessionsConnected: 0,
            videoCallsConnected: 0,
            callsConnected: 0,
            chatConnected: 0
          },
          weekly: {
            studentsConnected: 0,
            sessionsConnected: 0,
            videoCallsConnected: 0,
            callsConnected: 0,
            chatConnected: 0,
            counselorsOnboarded: 0,
            agenciesOnboarded: 0
          },
          monthly: {
            studentsConnected: 0,
            sessionsConnected: 0,
            videoCallsConnected: 0,
            callsConnected: 0,
            chatConnected: 0,
            counselorsOnboarded: 0,
            agenciesOnboarded: 0
          },
          totals: {
            agencies: 0,
            counselors: 0,
            students: 0,
            sessions: 0,
            videoCalls: 0,
            calls: 0,
            chats: 0
          }
        })
      } finally {
        setLoading(false)
      }
    }

    fetchStats()
  }, [])

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="gap-y-6 flex flex-col">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        {/*
        <div className="flex items-center space-x-2 text-sm text-gray-500">
          <Activity className="h-4 w-4" />
          <span>Live metrics</span>
        </div>
        */}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-red-800 text-sm">{error}</div>
        </div>
      )}

      {stats && (
        <>
          {/* Today's Stats */}
          <StatsGrid
            title="Today's Activity"
            stats={stats.today}
            loading={loading}
          />

          {/* This Week's Stats */}
          <StatsGrid
            title="This Week"
            stats={stats.weekly}
            loading={loading}
            showOnboarding={true}
          />

          {/* This Month's Stats */}
          <StatsGrid
            title="This Month"
            stats={stats.monthly}
            loading={loading}
            showOnboarding={true}
          />

          {/* Total Counts */}
          <div className="mb-8">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Total Platform Statistics</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                title="Total Agencies"
                value={stats.totals.agencies}
                icon={Building2}
                color="blue"
                loading={loading}
              />
              <StatCard
                title="Total Counselors"
                value={stats.totals.counselors}
                icon={Users}
                color="green"
                loading={loading}
              />
              <StatCard
                title="Total Students"
                value={stats.totals.students}
                icon={GraduationCap}
                color="purple"
                loading={loading}
              />
              <StatCard
                title="Total Sessions"
                value={stats.totals.sessions}
                icon={Calendar}
                color="orange"
                loading={loading}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <StatCard
                title="Total Video Calls"
                value={stats.totals.videoCalls}
                icon={Video}
                color="purple"
                loading={loading}
              />
              <StatCard
                title="Total Audio Calls"
                value={stats.totals.calls}
                icon={Phone}
                color="orange"
                loading={loading}
              />
              <StatCard
                title="Total Chats"
                value={stats.totals.chats}
                icon={MessageCircle}
                color="red"
                loading={loading}
              />
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default Dashboard